import { Box, Tooltip, Typography } from "@mui/joy";
import { AlertTriangle } from "lucide-react";

import { FormInput } from "../../../../shared";
import type { MeasurementRowData } from "./utils";
import {
    formatDifferenceLabel,
    formatMeasurementValue,
    hasVariance,
    MEASUREMENT_EDGE_PADDING,
} from "./utils";

type MeasurementRowProps = {
    row: MeasurementRowData;
    loading?: boolean;
    showDivider?: boolean;
};

export function MeasurementRow({
    row,
    loading = false,
    showDivider = true,
}: MeasurementRowProps) {
    const { key, label, unit, calculated, difference } = row;
    const rowHasVariance = hasVariance(row);
    const diffLabel = formatDifferenceLabel(difference, rowHasVariance);

    return (
        <Box
            sx={{
                borderBottom: showDivider ? "1px solid" : "none",
                borderColor: "divider",
            }}
        >
            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: {
                        xs: "1fr auto",
                        md: "minmax(160px, 1.4fr) minmax(96px, 1fr) minmax(132px, 1.15fr) 100px",
                    },
                    gridTemplateAreas: {
                        xs: `"label diff"
                             "calculated calculated"
                             "actual actual"`,
                        md: `"label calculated actual diff"`,
                    },
                    columnGap: { xs: 1.5, md: 2.5 },
                    rowGap: { xs: 0.5, md: 0 },
                    alignItems: { xs: "stretch", md: "center" },
                    py: { xs: 0.75, md: 0.75 },
                    px: MEASUREMENT_EDGE_PADDING,
                }}
            >
                <Box sx={{ gridArea: "label", minWidth: 0, pr: { md: 1.5 } }}>
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.75,
                            minWidth: 0,
                        }}
                    >
                        <Typography level="title-sm" sx={{ minWidth: 0 }}>
                            {label}{" "}
                            <Typography
                                component="span"
                                level="body-xs"
                                color="neutral"
                            >
                                ({unit})
                            </Typography>
                        </Typography>
                        {rowHasVariance && (
                            <Tooltip
                                title="Actual differs from calculated"
                                placement="top"
                                arrow
                            >
                                <Box
                                    component="span"
                                    sx={{
                                        display: "inline-flex",
                                        alignItems: "center",
                                        color: "warning.500",
                                        flexShrink: 0,
                                    }}
                                >
                                    <AlertTriangle
                                        size={14}
                                        strokeWidth={2.25}
                                    />
                                </Box>
                            </Tooltip>
                        )}
                    </Box>
                </Box>

                <Box
                    sx={{
                        gridArea: "calculated",
                        display: "flex",
                        flexDirection: { xs: "row", md: "row" },
                        alignItems: "center",
                        gap: { xs: 1, md: 0 },
                        justifyContent: { md: "flex-end" },
                        minWidth: 0,
                        pr: { md: 2 },
                    }}
                >
                    <Typography
                        level="body-xs"
                        color="neutral"
                        sx={{
                            display: { md: "none" },
                            minWidth: 72,
                            textTransform: "uppercase",
                        }}
                    >
                        Calculated
                    </Typography>
                    <Typography
                        level="body-xs"
                        color="neutral"
                        sx={{
                            fontVariantNumeric: "tabular-nums",
                            textAlign: { md: "right" },
                        }}
                    >
                        {formatMeasurementValue(calculated)}
                    </Typography>
                </Box>

                <Box
                    sx={{
                        gridArea: "actual",
                        minWidth: 0,
                        alignSelf: "stretch",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        pl: { md: 3 },
                        pr: { md: 1.5 },
                        borderTop: { xs: "1px solid", md: "none" },
                        borderLeft: {
                            md: "1px solid var(--standard-palette-divider)",
                        },
                        pt: { xs: 0.5, md: 0 },
                    }}
                >
                    <Typography
                        level="body-xs"
                        color="neutral"
                        sx={{
                            display: { md: "none" },
                            mb: 0.25,
                            textTransform: "uppercase",
                        }}
                    >
                        Actual
                    </Typography>
                    <FormInput
                        name={`Measurements[${key}].Actual`}
                        type="number"
                        placeholder="0"
                        disabled={loading}
                        sx={{
                            fontSize: "md",
                        }}
                    />
                </Box>

                <Box
                    sx={{
                        gridArea: "diff",
                        display: "flex",
                        justifyContent: { xs: "flex-end", md: "center" },
                        alignItems: "center",
                    }}
                >
                    <Typography level="body-sm">{diffLabel}</Typography>
                </Box>
            </Box>
        </Box>
    );
}

type MeasurementTableHeaderProps = {
    show?: boolean;
};

export function MeasurementTableHeader({
    show = true,
}: MeasurementTableHeaderProps) {
    if (!show) return null;

    return (
        <Box
            sx={{
                display: { xs: "none", md: "grid" },
                gridTemplateColumns:
                    "minmax(160px, 1.4fr) minmax(96px, 1fr) minmax(132px, 1.15fr) 100px",
                columnGap: 2.5,
                px: MEASUREMENT_EDGE_PADDING,
                py: 0.75,
                borderTop: "1px solid",
                borderBottom: "1px solid",
                borderColor: "divider",
            }}
        >
            {["Measurement", "Calculated", "Actual", "Diff"].map((heading) => (
                <Typography
                    key={heading}
                    level="body-xs"
                    sx={{
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        textAlign:
                            heading === "Diff"
                                ? "center"
                                : heading === "Calculated"
                                  ? "right"
                                  : "left",
                        ...(heading === "Actual"
                            ? {
                                  borderLeft: "1px solid",
                                  borderColor: "divider",
                                  pl: 3,
                              }
                            : {}),
                        ...(heading === "Calculated" ? { pr: 2 } : {}),
                    }}
                >
                    {heading}
                </Typography>
            ))}
        </Box>
    );
}
